import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    Button,
    Paper,
    Grid,
    Card,
    IconButton,
    Chip,
    LinearProgress,
    Pagination,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import useMediaQuery from '@mui/material/useMediaQuery';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Visibility from '@mui/icons-material/Visibility';
import Search from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import { useDispatch, useSelector } from "react-redux";
import { getAllSalesTargetsAction, getAllSeasonsAction, getAllDistrictsAction, getAllMandalsAction, getAllVarietiesAction, getAllCropsAction,calculateSalesTargetAction, } from "../../store/actions";
import { getAllZonesAction, getAllRegionsAction, getAllAreasAction } from "../../store/actions";
import SalesTargetDetailsView from './SalesTargetDetailsView';

const SalesTarget = () => {

    // Add zone, region, area selectors
    const { successData: zonesData } = useSelector((state) => state.getAllZonesReducer || {});
    const { successData: regionsData } = useSelector((state) => state.getAllRegionsReducer || {});
    const { successData: areasData } = useSelector((state) => state.getAllAreasReducer || {});
    

    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.only('xs'));
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [locationType, setLocationType] = useState("All");
    const [seasonFilter, setSeasonFilter] = useState("All");
    const [districtFilter, setDistrictFilter] = useState("All");
    const [mandalFilter, setMandalFilter] = useState("All");
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [viewRow, setViewRow] = useState(null);
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 30;

    useEffect(() => {
        dispatch(getAllSalesTargetsAction());
        dispatch(getAllSeasonsAction());
        dispatch(getAllDistrictsAction());
        dispatch(getAllMandalsAction());
        dispatch(getAllZonesAction());
        dispatch(getAllRegionsAction());
        dispatch(getAllAreasAction());
        dispatch(getAllVarietiesAction());
        dispatch(getAllCropsAction());
    }, [dispatch]);

    const { successData: targetsSuccess } = useSelector((state) => state.getAllSalesTargetsReducer || {});
    const { successData: seasons } = useSelector((state) => state.getAllSeasonsReducer || {});
    const { successData: districts } = useSelector((state) => state.getAllDistrictsReducer || {});
    const { successData: mandals } = useSelector((state) => state.getAllMandalsReducer || {});
    const { successData: cropsData } = useSelector((state) => state.getAllCropsReducer || {});
    const { successData: calcResponse } = useSelector((state) => state.calculateSalesTargetReducer || {});

    // Get varieties from Redux
    const { successData: varietiesData } = useSelector((state) => state.getAllVarietiesReducer || {});
    const varietiesList = varietiesData?.data?.varieties || [];
    // Helper to get UOM (quantity_type) for a varietyId
    const getUOMByVarietyId = (varietyId) => {
        const variety = varietiesList.find(v => v.varietyId === varietyId);
        return variety ? variety.quantity_type : '';
    };

    const targets = targetsSuccess?.data || [];
    const seasonList = seasons?.data?.seasons || [];
    const districtList = districts?.data?.districts || [];
    const mandalList = mandals?.data?.mandals || [];
    const zoneList = zonesData?.data?.zones || [];
    const regionList = regionsData?.data?.regions || [];
    const areaList = areasData?.data?.areas || [];
    const cropList = cropsData?.data?.crops || [];
    // Add zone, region, area filter states
    const [zoneFilter, setZoneFilter] = useState("All");
    const [regionFilter, setRegionFilter] = useState("All");
    const [areaFilter, setAreaFilter] = useState("All");
    const [recalculatedAcres, setRecalculatedAcres] = useState({});
    const [currentCalcId, setCurrentCalcId] = useState(null);


    const getStatusChipStyles = (status) => {
        switch (status) {
            case 'Active':
                return { backgroundColor: '#D1FAE5', color: '#047857' }; 
            case 'Pending':
                return { backgroundColor: '#FFEFD6', color: '#B45309' }; 
            case 'Inactive':
                return { backgroundColor: '#FEE2E2', color: '#B91C1C' }; 
            default:
                return {};
        }
    };

    const getStatus = (t) => {
        if (t?.season?.is_current === true) return 'Active';
        if (t?.season?.is_current === false) return 'Inactive';
        return 'Pending';
    };
    // Consistent desktop grid column widths for perfect alignment
    const gridColsDesktop = '0.9fr 1.3fr 1.0fr 1.2fr 1.2fr 0.9fr 0.8fr';
    const isIpad = useMediaQuery('(min-width:768px) and (max-width:1024px)');



    const stats = [
        { label: "TOTAL TARGETS", value: "0", icon: <TrackChangesIcon />, bg: '#EEF4FF' },
        { label: "ACTIVE TARGETS", value: "0", icon: <CheckCircleIcon />, bg: '#E6FBF4' },
        { label: "INACTIVE TARGETS", value: "0", icon: <CancelIcon />, bg: '#FFF1F1' },
        { label: "COMPLETION RATE", value: "0", icon: <DonutLargeIcon />, bg: '#F4F3FF' },
    ];

    const handleAddTarget = () => {
        navigate('/masterData/createSalesTarget');
    };

    const handleEditTarget = (t) => {
        const id = t?.id;
        if (id == null) return;
        navigate('/masterData/createSalesTarget', { state: { id } });
    };

    const handleViewTarget = (t) => {
        setViewRow({
            season: getSeasonDisplayName(t),
            areaType: t?.target_area_type || '-',
            areaName: getAreaName(t) || '-',
            salesTarget: formatQty(t?.total_sales_target_qty),
            avg_qty_per_acre: t?.avg_qty_per_acre,
            avg_farmer_purch_qty: t?.avg_farmer_purch_qty,
            farmerTarget: t?.farmer_comm_acre_required != null ? formatQty(t?.farmer_comm_acre_required) : '-',
            targetDone: t?.no_acres_comm != null ? formatQty(t?.no_acres_comm) : '-',
            salesTargetDetails: Array.isArray(t?.salesTargetDetails) ? t.salesTargetDetails : [],
        });
        setOpenViewDialog(true);
    };

    const handleCloseViewDialog = () => {
        setOpenViewDialog(false);
        setViewRow(null);
    };

    // Helper to get area name by type and id
    const getAreaName = (t) => {
        if (!(t?.target_area_type && t?.target_area_id)) return '';
        const type = t.target_area_type.toLowerCase();
        if (type === 'mandal') {
            const mandal = mandalList.find(m => m.mandalId === t.target_area_id);
            if (mandal) {
                return `${mandal.districtName || ''} - ${mandal.mandalName || mandal.name || ''}`;
            }
            if (t.target_area?.mandal_name && t.target_area?.district_name) {
                return `${t.target_area.district_name} - ${t.target_area.mandal_name}`;
            }
            return t.target_area_id;
        }
        if (type === 'district') {
            const district = districtList.find(d => d.districtId === t.target_area_id);
            if (district) {
                return district.name || '';
            }
            if (t.target_area?.district_name) {
                return t.target_area.district_name;
            }
            return t.target_area_id;
        }
        if (type === 'zone') {
            if (t.target_area?.zone_name) {
                return t.target_area.zone_name;
            }
            // fallback: try to find in districtList or mandalList if possible
            return t.target_area_id;
        }
        if (type === 'region') {
            if (t.target_area?.region_name) {
                return t.target_area.region_name;
            }
            return t.target_area_id;
        }
        if (type === 'area') {
            if (t.target_area?.area_name) {
                return t.target_area.area_name;
            }
            return t.target_area_id;
        }
        if (t.target_area?.name) {
            return t.target_area.name;
        }
        return t.target_area_id ?? '';
    };

    // Helper to format quantity: remove trailing .00
    const formatQty = (val) => {
        const num = parseFloat(val);
        if (isNaN(num)) return val;
        return num % 1 === 0 ? num.toFixed(0) : num.toString();
    };

    const getTargetAcresProgress = (target) => {
        const done = parseFloat(target?.no_acres_comm || 0);
        const required = parseFloat(target?.farmer_comm_acre_required || 0);
        if (required === 0) return 0;
        return Math.round((done / required) * 100);
    };

    const getProgressBarColor = (percentage) => {
        if (percentage >= 100) return '#10B981'; // Green for 100%+
        if (percentage >= 75) return '#3B82F6'; // Blue for 75-99%
        if (percentage >= 50) return '#F59E0B'; // Orange for 50-74%
        return '#EF4444'; // Red for <50%
    };
    // Helper to compute Target value = total_sales_target_qty - no_acres_comm
    // If no_acres_comm is null/0 → show total_sales_target_qty; if result negative → show 0
    const getTargetValue = (t) => {
        const total = parseFloat(t?.total_sales_target_qty) || 0;
        const noAcres = parseFloat(t?.no_acres_comm) || 0;
        if (!noAcres) return formatQty(total);
        const result = total - noAcres;
        return formatQty(result < 0 ? 0 : result);
    };

    // Helper to get season display name
    const getSeasonDisplayName = (t) => {
        if (t.season && (t.season.name || t.season.season_name)) {
            const name = t.season.name || t.season.season_name;
            const year = t.season.year || t.season.season_year || '';
            return year ? `${name} (${year})` : name;
        }
        let seasonId = t.seasonId ?? t.season_id;
        const found = seasonList.find(s => (s.seasonId || s.id) === seasonId || s.id === seasonId);
        if (found) {
            const name = found.name || found.season_name;
            const year = found.year || found.season_year || '';
            return year ? `${name} (${year})` : name;
        }
        return t.seasonId ?? t.season_id;
    };

    // Show all data from API, only filter by search (not by type/locationType)
    const filteredTargets = useMemo(() => {
        const q = search.trim().toLowerCase();
        return targets.filter(t => {
            const seasonName = t?.season?.name || '';
            const areaName = String(getAreaName(t) || '').toLowerCase();
            // Search
            if (q) {
                const hay = [seasonName, t?.target_area_type, areaName, String(t?.py_total_sales_qty || ''), String(t?.total_sales_target_qty || '')].join(' ').toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [targets, search]);

    // Reset page when filters change
    useEffect(() => { setPage(1); }, [search, seasonFilter, districtFilter, zoneFilter, regionFilter, areaFilter]);

    const totalPages = Math.max(1, Math.ceil(filteredTargets.length / PAGE_SIZE));
    const pagedTargets = filteredTargets.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

     // Fetch master data only once on mount
    useEffect(() => {
        dispatch(getAllSeasonsAction());
        dispatch(getAllDistrictsAction());
        dispatch(getAllMandalsAction());
        dispatch(getAllZonesAction());
        dispatch(getAllRegionsAction());
        dispatch(getAllAreasAction());
        dispatch(getAllVarietiesAction());
        dispatch(getAllCropsAction());
    }, [dispatch]);

    // Fetch sales targets when filters change
    useEffect(() => {
        const queryParams = {};
        if (zoneFilter !== 'All') queryParams.zoneId = zoneFilter;
        if (regionFilter !== 'All') queryParams.regionId = regionFilter;
        if (areaFilter !== 'All') queryParams.areaId = areaFilter;
        if (districtFilter !== 'All') queryParams.districtId = districtFilter;
        if (seasonFilter !== 'All') queryParams.seasonId = seasonFilter;
        // For debugging
        console.log('Query Params sent to backend:', queryParams);
        // Build endpoint string (optional)
        const paramsArr = [];
        if (seasonFilter !== 'All') paramsArr.push(`seasonId=${seasonFilter}`);
        if (zoneFilter !== 'All') paramsArr.push(`zoneId=${zoneFilter}`);
        if (regionFilter !== 'All') paramsArr.push(`regionId=${regionFilter}`);
        if (areaFilter !== 'All') paramsArr.push(`areaId=${areaFilter}`);
        if (districtFilter !== 'All') paramsArr.push(`districtId=${districtFilter}`);
        const query = paramsArr.length ? `target/sales?${paramsArr.join('&')}` : 'target/sales';
        dispatch(getAllSalesTargetsAction({ params: queryParams, endPoint: query }));
    }, [dispatch, zoneFilter, regionFilter, areaFilter, districtFilter, seasonFilter]);

   const handleRecalculate = (t) => {
    if (!t) return;

    const payload = {
        id: t.id,
        seasonId: t.season_id || t.seasonId || t?.season?.season_id,
        areaType: (t.target_area_type || '').toLowerCase(), // must be lowercase
        areaId: t.target_area_id
    };

    console.log("Recalculate Payload:", payload);
     setCurrentCalcId(t.id);

    dispatch(calculateSalesTargetAction({ ...payload, localId: t.id }));
};

useEffect(() => {
  if (calcResponse?.data?.total_expected_acre && currentCalcId) {
    setRecalculatedAcres((prev) => ({
      ...prev,
      [currentCalcId]: calcResponse.data.total_expected_acre
    }));
  }
}, [calcResponse]);

    return (
        <Box  
            sx={{
                minHeight: '100vh',

            }}
        >
            {/* PAGE HEADER */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: { sm: 7,md:0 } }}>
                Sales Target
            </Typography>

            {/* STATS CARDS */}
            {/* <Box sx={{ width: '100%', mb: 3 }}>
                <Grid
                    container
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                        width: '100%',
                        m: 0,

                    }}
                >
                    {stats.map((s) => (
                        <Grid
                            item
                            size={{ xs: 6, sm: 3, md: 3 }}
                            key={s.label}
                            sx={{
                                p: 0,
                                display: 'flex',
                                justifyContent: 'stretch',
                            }}
                        >
                            <Card sx={{ p: isIpad ? 1.5 : 2, borderRadius: 3, height: isIpad ? 60 : 50, width: '100%', mx: 0, display: 'flex', alignItems: 'center' }}>
                                <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
                                    <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                fontSize: { xs: '0.85rem', sm: 'inherit' },
                                                textAlign: 'left',
                                                mb: 0,
                                            }}
                                        >
                                            {s.label}
                                        </Typography>
                                        <Typography
                                            variant="h5"
                                            fontWeight={600}
                                            sx={{
                                                fontSize: { xs: '1.1rem', sm: isIpad ? '1.4rem' : '1.5rem' },
                                                textAlign: 'left',
                                                mb: 0,
                                            }}
                                        >
                                            {s.value}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            bgcolor: s.bg,
                                            width: isIpad ? 44 : 45,
                                            height: isIpad ? 44 : 45,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: 2,
                                        }}
                                    >
                                        {s.icon}
                                    </Box>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box> */}



            {/* FILTER SECTION */}
            <Card sx={{ mr: { xs: 1 }, p: 2, borderRadius: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item size={{ xs: 12, sm: 4, md: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Search ..."
                            slotProps={{
                                input: {
                                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                                },
                            }}
                            fullWidth
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                },
                            }}
                        />
                    </Grid>

                    <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
                        <Select size="small" value={locationType} onChange={(e) => setLocationType(e.target.value)} sx={{ width: '100%', borderRadius: 3 }}>
                            <MenuItem value="All">Location Type</MenuItem>
                            <MenuItem value="District">District</MenuItem>
                            <MenuItem value="Zone">Zone</MenuItem>
                            <MenuItem value="Region">Region</MenuItem>
                            <MenuItem value="Area">Area</MenuItem>
                        </Select>
                    </Grid>
                    <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
                        <Select size="small" value={districtFilter} onChange={(e) => setDistrictFilter(e.target.value)} sx={{ width: '100%', borderRadius: 3 }}>
                            <MenuItem value="All">All Districts</MenuItem>
                            {districtList.map(d => (
                                <MenuItem key={d.districtId} value={d.districtId}>{d.name}</MenuItem>
                            ))}
                        </Select>
                    </Grid>
                    <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
                        <Select size="small" value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)} sx={{ width: '100%', borderRadius: 3 }}>
                            <MenuItem value="All">All Zones</MenuItem>
                            {zoneList.map(z => (
                                <MenuItem key={z.zoneId} value={z.zoneId}>{z.zoneName}</MenuItem>
                            ))}
                        </Select>
                    </Grid>
                    <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
                        <Select size="small" value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)} sx={{ width: '100%', borderRadius: 3 }}>
                            <MenuItem value="All">All Regions</MenuItem>
                            {regionList.map(r => (
                                <MenuItem key={r.regionId} value={r.regionId}>{r.regionName}</MenuItem>
                            ))}
                        </Select>
                    </Grid>
                    <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
                        <Select size="small" value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)} sx={{ width: '100%', borderRadius: 3 }}>
                            <MenuItem value="All">All Areas</MenuItem>
                            {areaList.map(a => (
                                <MenuItem key={a.areaId} value={a.areaId}>{a.areaName}</MenuItem>
                            ))}
                        </Select>
                    </Grid>
                    <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
                        <Select size="small" value={seasonFilter} onChange={(e) => setSeasonFilter(e.target.value)} sx={{ width: '100%', borderRadius: 3 }}>
                            <MenuItem value="All">All Seasons</MenuItem>
                            {seasonList.map(s => (
                                <MenuItem key={s.seasonId || s.id || s.season_id} value={s.seasonId || s.id || s.season_id}>
                                    {s.name}{s.year ? ` (${s.year})` : ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 4, md: 2 }}>
                        <Button
                            variant="contained"
                            sx={{
                                borderRadius: 2,
                                whiteSpace: 'nowrap',
                                '@media (min-width:1100px) and (max-width:1500px)': {
                                    fontSize: '0.70rem',
                                },
                            }}
                            fullWidth
                            onClick={handleAddTarget}
                        >
                            + Add New Target
                        </Button>
                    </Grid>
                </Grid>
            </Card>

            {/* CONTENT AREA */}
            <Box sx={{ mt: 3 }}>
                {isMobile ? (
                    <Grid container spacing={2}>
                        {pagedTargets.map((t) => (
                            <Grid item size={{ xs: 12 }} key={t.id}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        borderRadius: 3,
                                        p: 1.5,
                                        border: '1px solid #E5E7EB',
                                        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                        '&:hover, &:active': {
                                            borderColor: '#2563EB',
                                            boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                        },
                                    }}
                                >
                                    <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                                        <Typography fontWeight={600} minWidth={88}>Season:</Typography>
                                        <Typography variant="body2">{getSeasonDisplayName(t)}</Typography>
                                        <Box ml="auto">
                                            <Chip
                                                label={getStatus(t)}
                                                size="small"
                                                sx={{
                                                    ...getStatusChipStyles(getStatus(t)),
                                                    height: 24,
                                                    '& .MuiChip-label': { fontSize: 11, fontWeight: 600 },
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                                        <Typography fontWeight={600} minWidth={88}>Area Type:</Typography>
                                        <Typography variant="body2">{t?.target_area_type}</Typography>
                                    </Box>

                                    <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                                        <Typography fontWeight={600} minWidth={88}>Area Name:</Typography>
                                        <Typography variant="body2">{getAreaName(t)}</Typography>
                                    </Box>

                                    <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                                        <Typography fontWeight={600} minWidth={88}>Target Acres:</Typography>
                                        <Box sx={{ flex: 1 }}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography variant="body2">
{recalculatedAcres[t.id] !== undefined
  ? formatQty(recalculatedAcres[t.id])
  : (t?.no_acres_comm != null ? formatQty(t.no_acres_comm) : '-')
}                                                    /
                                                    {t?.farmer_comm_acre_required !== undefined && t?.farmer_comm_acre_required !== null ? formatQty(t.farmer_comm_acre_required) : '-'}
                                                </Typography>
                                                <IconButton
        size="small"
        onClick={() => handleRecalculate(t)}
        sx={{
            p: 0.5,
            ml: 0.5
        }}
    >
        <RefreshIcon sx={{ fontSize: 16, color: '#2563EB' }} />
    </IconButton>
                                                <Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                        color: getProgressBarColor(getTargetAcresProgress(t)),
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    {getTargetAcresProgress(t)}%
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={Math.min(getTargetAcresProgress(t), 100)}
                                                sx={{
                                                    mt: 0.5,
                                                    height: 8,
                                                    borderRadius: 4,
                                                    backgroundColor: '#E5E7EB',
                                                    '& .MuiLinearProgress-bar': {
                                                        backgroundColor: getProgressBarColor(getTargetAcresProgress(t)),
                                                        borderRadius: 4,
                                                        transition: 'all 0.3s ease',
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                                        <Typography fontWeight={600} minWidth={88}>Total Target Qty:</Typography>
                                        <Typography variant="body2">
                                            {formatQty(t?.total_sales_target_qty)}
                                            {Array.isArray(t.salesTargetDetails) && t.salesTargetDetails.length > 0 && t.salesTargetDetails[0].variety_id && getUOMByVarietyId(t.salesTargetDetails[0].variety_id) && (
                                                <small style={{ marginLeft: 4, color: '#888' }}>{getUOMByVarietyId(t.salesTargetDetails[0].variety_id)}</small>
                                            )}
                                        </Typography>
                                    </Box>

                                    <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1} mt={1}>
                                        <IconButton size="small" aria-label="view" onClick={() => handleViewTarget(t)}>
                                            <Visibility fontSize="small" sx={{ color: '#036144', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleEditTarget(t)}>
                                            <EditOutlinedIcon fontSize="small" sx={{ color: '#2563EB', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                                        </IconButton>
                                    </Box>
                                </Paper>
                            </Grid>
                        ))}
                        {filteredTargets.length === 0 && (
                            <Grid item size={{ xs: 12 }}>
                                <Typography variant="body2" color="text.secondary">No sales targets found.</Typography>
                            </Grid>
                        )}
                        {totalPages > 1 && (
                            <Grid item size={{ xs: 12 }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" mt={1} flexWrap="wrap" gap={1}>
                                    <Typography variant="body2" >
                                        Showing {Math.min((page - 1) * PAGE_SIZE + 1, filteredTargets.length)} to {Math.min(page * PAGE_SIZE, filteredTargets.length)} of {filteredTargets.length} entries
                                    </Typography>
                                    <Pagination count={totalPages} page={page} onChange={(_, val) => setPage(val)} color="primary" size="small" />
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                ) : (
                    <Box display="flex" flexDirection="column" gap={1}>
                        {/* Desktop header row */}
                        <Box
                            elevation={0}
                            sx={{
                                display: { xs: 'none', sm: 'grid' },
                                gridTemplateColumns: { sm: gridColsDesktop },
                                alignItems: 'center',
                                px: 1.2,
                                py: 1,
                                gap: 0.5,
                                columnGap: 0.5,
                            }}
                        >
                            {['Season Name', 'Area Type', 'Area Name', 'Target Acres', 'Total Target ', 'Status', 'Actions'].map((h) => (
                                <Typography
                                    key={h}
                                    variant="caption"
                                    fontWeight={600}
                                    sx={{ textTransform: 'uppercase', textAlign: h === 'Actions' ? 'right' : 'left' }}
                                >
                                    {h}
                                </Typography>
                            ))}
                        </Box>

                        {/* Desktop value rows */}
                        {pagedTargets.map((t) => (
                            <Paper
                                key={t.id}
                                elevation={0}
                                sx={{
                                    borderRadius: 3,
                                    display: { xs: 'none', sm: 'grid' },
                                    gridTemplateColumns: { sm: gridColsDesktop },
                                    alignItems: 'center',
                                    border: '1px solid #E5E7EB',
                                    px: 2,
                                    py: 1.5,
                                    gap: 0,
                                    columnGap: 0,
                                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                    '&:hover': {
                                        borderColor: '#2563EB',
                                        boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                    },
                                }}
                            >
                                <Typography  variant="body2">{getSeasonDisplayName(t)}</Typography>
                                <Typography ml={3} variant="body2">{t?.target_area_type}</Typography>
                                <Typography variant="body2">{getAreaName(t)}</Typography>
                               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
  
  {/* Top row: acres text + percentage only */}
  <Box display="flex" alignItems="center" gap={1}>
    <Typography variant="body2">
      {recalculatedAcres[t.id] !== undefined
  ? formatQty(recalculatedAcres[t.id])
  : (t?.no_acres_comm != null ? formatQty(t.no_acres_comm) : '-')
}
      /
      {t?.farmer_comm_acre_required !== undefined && t?.farmer_comm_acre_required !== null ? formatQty(t.farmer_comm_acre_required) : '-'}
    </Typography>

    <Typography
      variant="caption"
      sx={{
        color: getProgressBarColor(getTargetAcresProgress(t)),
        fontWeight: 600
      }}
    >
      {getTargetAcresProgress(t)}%
    </Typography>
  </Box>

  {/* Bottom row: progress bar + refresh icon */}
  <Box display="flex" alignItems="center" gap={0.5}>
    <Box sx={{ flex: 1 }}>
      <LinearProgress
        variant="determinate"
        value={Math.min(getTargetAcresProgress(t), 100)}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: '#E5E7EB',
          '& .MuiLinearProgress-bar': {
            backgroundColor: getProgressBarColor(getTargetAcresProgress(t)),
            borderRadius: 4,
            transition: 'all 0.3s ease',
          },
        }}
      />
    </Box>
    <Button size="small" sx={{ minWidth: 32, borderColor: 'black', color: '#2563EB', '&:hover': { borderColor: '#2563EB', backgroundColor: 'rgba(37,99,235,0.08)' } }} >
    <IconButton
      size="small"
      onClick={() => handleRecalculate(t)}
      sx={{ p: 0.5, flexShrink: 0 }}
    >
      <RefreshIcon sx={{ fontSize: 16, color: '#2563EB' }} />
    </IconButton>
    </Button>
  </Box>

</Box>
                                <Typography variant="body2">
                                    {formatQty(t?.total_sales_target_qty)}
                                    {Array.isArray(t.salesTargetDetails) && t.salesTargetDetails.length > 0 && t.salesTargetDetails[0].variety_id && getUOMByVarietyId(t.salesTargetDetails[0].variety_id) && (
                                        <small style={{ marginLeft: 4, color: '#888' }}>{getUOMByVarietyId(t.salesTargetDetails[0].variety_id)}</small>
                                    )}
                                </Typography>

                                <Chip
                                    label={getStatus(t)}
                                    size="small"
                                    sx={{
                                        width: 90,
                                        height: 24,
                                        ml: -2,
                                        ...getStatusChipStyles(getStatus(t)),
                                        '& .MuiChip-label': { fontSize: 12, fontWeight: 600 },
                                    }}
                                />

                                <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end">
                                    <IconButton size="small" aria-label="view" onClick={() => handleViewTarget(t)}>
                                        <Visibility fontSize="small" sx={{ color: '#036144', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                                    </IconButton>
                                    <IconButton size="small" aria-label="edit" onClick={() => handleEditTarget(t)}>
                                        <EditOutlinedIcon fontSize="small" sx={{ color: '#2563EB', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                                    </IconButton>
                                </Box>
                            </Paper>
                        ))}
                        {filteredTargets.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>No sales targets found.</Typography>
                        )}
                        {totalPages > 1 && (
                            <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} flexWrap="wrap" gap={1}>
                                <Typography variant="body2" >
                                    Showing {Math.min((page - 1) * PAGE_SIZE + 1, filteredTargets.length)} to {Math.min(page * PAGE_SIZE, filteredTargets.length)} of {filteredTargets.length} entries
                                </Typography>
                                <Pagination count={totalPages} page={page} onChange={(_, val) => setPage(val)} color="primary" />
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            <SalesTargetDetailsView
                open={openViewDialog}
                onClose={handleCloseViewDialog}
                row={viewRow}
                cropList={cropList}
                varietyList={varietiesList}
            />
        </Box>
    );
};
export default SalesTarget;